import { useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight, ClipboardCheck, ListChecks, Search, ShieldCheck } from 'lucide-react'
import type { PendingReview } from '@/api/formsApi'
import type { AssignedProcessStep, ProcessStepExecutionStatus } from '@/api/processStepsApi'
import { ColumnCells, ResizableTableHead, RowActions } from '@/components/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAssignedProcessSteps } from '@/hooks/useProcessSteps'
import { usePendingReviews } from '@/hooks/usePendingReviews'
import { useSortedRows } from '@/hooks/useSortedRows'
import { useTableColumns } from '@/hooks/useTableColumns'
import {
  REVIEW_FILTERS,
  countLabel,
  formatDateTime,
  matchesSearch,
  myWorkTab,
  readyFirst,
  reviewStatus,
  stepStatus,
  workSubject,
  type MyWorkTab,
  type ReviewFilter,
  type WorkStatus,
} from '@/lib/myWork'
import { PERMISSIONS, useOptionalPermission } from '@/lib/permissions'
import { PROCESS_STEP_STATUS_META } from '@/lib/processStepStatus'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { cn } from '@/lib/utils'
import { versionLabel } from '@/lib/versioning'

/**
 * My Work: the reviews and the process steps assigned to the signed-in user,
 * one tab each, laid out like the app's other tabbed pages (Inventory,
 * Equipment). A tab appears only for someone who can act on it: reviews need
 * submissions.review, steps process.execute.
 */
export function MyProcessStepsPage() {
  const { can } = useOptionalPermission()
  const reviews = usePendingReviews()
  const canReview = reviews.canReview
  const canExecute = can(PERMISSIONS.processExecute)
  const steps = useAssignedProcessSteps({ enabled: canExecute })
  const [params, setParams] = useSearchParams()
  const tab = myWorkTab(params.get('tab'), canReview, canExecute)

  function setTab(next: string) {
    const sp = new URLSearchParams(params)
    sp.set('tab', next as MyWorkTab)
    setParams(sp, { replace: true })
  }

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='w-full px-6 py-8'>
        <div className='mb-5 flex items-center gap-3'>
          <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary'>
            <ClipboardCheck className='h-5 w-5 text-primary-foreground' />
          </div>
          <h1 className='text-2xl font-semibold tracking-tight text-gray-900'>My Work</h1>
        </div>

        {(canReview || canExecute) && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {canReview && (
                <TabsTrigger value='reviews' className='gap-2'>
                  <ShieldCheck className='h-4 w-4' />
                  Reviews
                  {reviews.isSuccess && <TabCount count={reviews.data.length} />}
                </TabsTrigger>
              )}
              {canExecute && (
                <TabsTrigger value='steps' className='gap-2'>
                  <ListChecks className='h-4 w-4' />
                  Process steps
                  {steps.isSuccess && <TabCount count={steps.data.length} />}
                </TabsTrigger>
              )}
            </TabsList>
            {canReview && (
              <TabsContent value='reviews' className='mt-4'>
                <ReviewsTab query={reviews} />
              </TabsContent>
            )}
            {canExecute && (
              <TabsContent value='steps' className='mt-4'>
                <StepsTab query={steps} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </div>
  )
}

// ─── Shared pieces ──────────────────────────────────────────────────────────

function TabCount({ count }: { count: number }) {
  return <span className='rounded-full bg-foreground/10 px-1.5 text-[11px] tabular-nums'>{count}</span>
}

/**
 * A tab's body, the same card as Inventory's: a toolbar with a search box and a
 * status filter, then the table, its empty or loading line, and the count.
 */
function WorkCard({
  search,
  onSearch,
  placeholder,
  filter,
  children,
}: {
  search: string
  onSearch: (q: string) => void
  placeholder: string
  filter: ReactNode
  children: ReactNode
}) {
  return (
    <div className='overflow-hidden rounded-xl border bg-card shadow-sm'>
      <div className='flex flex-wrap items-center gap-3 border-b p-4'>
        <div className='relative min-w-[240px] flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder={placeholder}
            className='pl-9'
            autoComplete='off'
          />
        </div>
        {filter}
      </div>
      <div className='p-4'>{children}</div>
    </div>
  )
}

function Note({ children, error }: { children: ReactNode; error?: boolean }) {
  return <p className={cn('py-12 text-center text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>{children}</p>
}

function StatusChip({ status }: { status: WorkStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-transparent px-2 py-0.5 text-xs font-medium',
        status.chip,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', status.dot)} />
      {status.label}
    </span>
  )
}

/** The form's name with its version, as the procedure tables show a template's. */
function FormName({ name, version, repeatIndex }: { name: string | null | undefined; version: number | null | undefined; repeatIndex?: number | null }) {
  if (!name) return <span className='text-muted-foreground'>—</span>
  return (
    <div className='flex max-w-[20rem] items-baseline gap-1'>
      <span className='min-w-0 truncate' title={name}>
        {name}
      </span>
      <span className='shrink-0 text-xs text-muted-foreground'>
        ({versionLabel(version)}
        {repeatIndex != null && ` · Repeat ${repeatIndex}`})
      </span>
    </div>
  )
}

/** What a row belongs to: the id, and under it whether that is a patient, a kit or a work order. */
function RecordCell({ row }: { row: Parameters<typeof workSubject>[0] }) {
  const subject = workSubject(row)
  return (
    <TableCell>
      {subject ? (
        <>
          <div className='whitespace-nowrap font-mono text-xs font-semibold'>{subject.value}</div>
          <div className='text-xs text-muted-foreground'>{subject.label}</div>
        </>
      ) : (
        <span className='text-muted-foreground'>—</span>
      )}
    </TableCell>
  )
}

/** The procedure, with its work order under it unless the row is already named by it. */
function ProcedureCell({ row }: { row: Parameters<typeof workSubject>[0] & { procedureName?: string | null } }) {
  const run = row.workOrderNumber && workSubject(row)?.label !== 'Work order' ? row.workOrderNumber : null
  return (
    <TableCell>
      {row.procedureName ? (
        <>
          <div className='max-w-[18rem] truncate' title={row.procedureName}>
            {row.procedureName}
          </div>
          {run && <div className='text-xs text-muted-foreground'>{run}</div>}
        </>
      ) : (
        <span className='text-muted-foreground'>—</span>
      )}
    </TableCell>
  )
}

function DateCell({ at }: { at: string | null | undefined }) {
  return (
    <TableCell className='whitespace-nowrap text-xs text-muted-foreground'>{formatDateTime(at) ?? '—'}</TableCell>
  )
}

function OpenCell({ label, onOpen }: { label: string; onOpen: (() => void) | null }) {
  return (
    <TableCell>
      <RowActions>
        {onOpen && (
          <Button
            size='icon'
            variant='ghost'
            className='h-7 w-7 text-muted-foreground'
            aria-label={label}
            onClick={e => {
              e.stopPropagation()
              onOpen()
            }}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        )}
      </RowActions>
    </TableCell>
  )
}

/**
 * The standard table: resizable columns, a Columns menu, header sorting and a
 * per-user saved layout. A row opens its form, which is where a review is
 * signed and a step is worked.
 */
function WorkTable<T>({
  tableId,
  columns,
  sort,
  rows,
  rowKey,
  openPath,
  cells,
}: {
  tableId: string
  columns: TableColumnDef[]
  sort: SortAccessors<T>
  rows: T[]
  rowKey: (row: T) => string
  openPath: (row: T) => string | null
  cells: (row: T, open: (() => void) | null) => Record<string, ReactNode>
}) {
  const navigate = useNavigate()
  const cols = useTableColumns(tableId, columns)
  const sorted = useSortedRows(rows, cols.sort, sort)
  return (
    <div className='rounded-lg border'>
      <Table>
        <TableHeader>
          <TableRow className='hover:bg-transparent'>
            {cols.visible.map(({ def }) => (
              <ResizableTableHead
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={def.id === 'open' ? 'w-16' : undefined}
                resizable={def.id !== 'open'}
              >
                {def.header}
              </ResizableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(row => {
            const path = openPath(row)
            const open = path ? () => navigate(path) : null
            return (
              <TableRow
                key={rowKey(row)}
                className={cn(open && 'cursor-pointer')}
                onClick={open ?? undefined}
              >
                <ColumnCells columns={cols} cells={cells(row, open)} />
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

const formPath = (submissionId: string) => `/forms/${encodeURIComponent(submissionId)}`

// ─── Reviews ────────────────────────────────────────────────────────────────

// Released table id: changing it discards saved layouts.
const REVIEWS_TABLE_ID = 'my-work-reviews'

const REVIEW_COLUMNS: TableColumnDef[] = [
  { id: 'form', header: 'Form', locked: true, defaultWidth: 220, sortable: true },
  { id: 'record', header: 'Record', defaultWidth: 160, sortable: true },
  { id: 'procedure', header: 'Procedure', defaultWidth: 200, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 140, sortable: true },
  { id: 'completedBy', header: 'Completed by', defaultWidth: 150, sortable: true },
  { id: 'completedAt', header: 'Completed', defaultWidth: 170, sortable: true },
  { id: 'assignedAt', header: 'Assigned', defaultHidden: true, defaultWidth: 170, sortable: true },
  { id: 'open', header: 'Open', locked: true, defaultWidth: 56 },
]

const REVIEW_SORT: SortAccessors<PendingReview> = {
  form: r => r.formName,
  record: r => workSubject(r)?.value ?? null,
  procedure: r => r.procedureName,
  status: r => reviewStatus(r).label,
  completedBy: r => r.completedByName ?? r.completedBy,
  completedAt: r => r.completedAt,
  assignedAt: r => r.assignedAt,
}

function ReviewsTab({ query }: { query: ReturnType<typeof usePendingReviews> }) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ReviewFilter>('all')
  const all = readyFirst(query.data ?? [])
  const rows = all.filter(
    r =>
      (filter === 'all' || reviewStatus(r).value === filter) &&
      matchesSearch(search, [
        r.formName,
        r.fieldLabel,
        workSubject(r)?.value,
        r.procedureName,
        r.workOrderNumber,
        r.completedByName ?? r.completedBy,
      ]),
  )

  return (
    <WorkCard
      search={search}
      onSearch={setSearch}
      placeholder='Search form, patient, kit or procedure…'
      filter={
        <Select value={filter} onValueChange={v => setFilter(v as ReviewFilter)}>
          <SelectTrigger className='w-[180px] shrink-0' aria-label='Filter by status'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REVIEW_FILTERS.map(f => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    >
      {query.error ? (
        <Note error>{query.error instanceof Error ? query.error.message : 'Could not load your reviews.'}</Note>
      ) : !query.isSuccess ? (
        <Note>Loading your reviews…</Note>
      ) : all.length === 0 ? (
        <Note>No reviews are assigned to you.</Note>
      ) : (
        <>
          {rows.length > 0 ? (
            <WorkTable
              tableId={REVIEWS_TABLE_ID}
              columns={REVIEW_COLUMNS}
              sort={REVIEW_SORT}
              rows={rows}
              rowKey={r => `${r.submissionId}:${r.fieldId}`}
              openPath={r => formPath(r.submissionId)}
              cells={(r, open) => ({
                form: (
                  <TableCell>
                    <FormName name={r.formName} version={r.formVersion} repeatIndex={r.repeatIndex} />
                    {/* Every Review field is called "Review" unless its author
                        named it; only a name tells two reviews on one form apart. */}
                    {r.fieldLabel && r.fieldLabel !== 'Review' && (
                      <div className='text-xs text-muted-foreground'>{r.fieldLabel}</div>
                    )}
                  </TableCell>
                ),
                record: <RecordCell row={r} />,
                procedure: <ProcedureCell row={r} />,
                status: (
                  <TableCell>
                    <StatusChip status={reviewStatus(r)} />
                  </TableCell>
                ),
                completedBy: (
                  <TableCell className='whitespace-nowrap'>
                    {r.completedByName ?? r.completedBy ?? <span className='text-muted-foreground'>—</span>}
                  </TableCell>
                ),
                completedAt: <DateCell at={r.completedAt} />,
                assignedAt: <DateCell at={r.assignedAt} />,
                open: <OpenCell label={`Open ${r.formName}`} onOpen={open} />,
              })}
            />
          ) : (
            <Note>No reviews match these filters.</Note>
          )}
          <p className='mt-4 text-xs text-muted-foreground'>{countLabel(rows.length, all.length, 'review')}</p>
        </>
      )}
    </WorkCard>
  )
}

// ─── Process steps ──────────────────────────────────────────────────────────

// Released table id: changing it discards saved layouts.
const STEPS_TABLE_ID = 'my-work-steps'

const STEP_COLUMNS: TableColumnDef[] = [
  { id: 'step', header: 'Step', locked: true, defaultWidth: 200, sortable: true },
  { id: 'form', header: 'Form', defaultWidth: 200, sortable: true },
  { id: 'record', header: 'Record', defaultWidth: 160, sortable: true },
  { id: 'procedure', header: 'Procedure', defaultWidth: 200, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 120, sortable: true },
  { id: 'plannedStart', header: 'Planned start', defaultWidth: 170, sortable: true },
  { id: 'assignedAt', header: 'Assigned', defaultWidth: 170, sortable: true },
  { id: 'open', header: 'Open', locked: true, defaultWidth: 56 },
]

const STEP_SORT: SortAccessors<AssignedProcessStep> = {
  step: s => s.stepLabel ?? null,
  form: s => s.formName ?? null,
  record: s => workSubject(s)?.value ?? null,
  procedure: s => s.procedureName ?? null,
  status: s => stepStatus(s.status).label,
  plannedStart: s => s.plannedStart,
  assignedAt: s => s.assignedAt,
}

// A completed step leaves this list, so it is not offered as a filter.
const STEP_FILTERS = (Object.keys(PROCESS_STEP_STATUS_META) as ProcessStepExecutionStatus[]).filter(s => s !== 'COMPLETED')

function StepsTab({ query }: { query: ReturnType<typeof useAssignedProcessSteps> }) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ProcessStepExecutionStatus | 'all'>('all')
  const all = query.data ?? []
  const rows = all.filter(
    s =>
      (filter === 'all' || s.status === filter) &&
      matchesSearch(search, [s.stepLabel, s.formName, workSubject(s)?.value, s.procedureName, s.workOrderNumber]),
  )

  return (
    <WorkCard
      search={search}
      onSearch={setSearch}
      placeholder='Search step, form, patient, kit or procedure…'
      filter={
        <Select value={filter} onValueChange={v => setFilter(v as ProcessStepExecutionStatus | 'all')}>
          <SelectTrigger className='w-[180px] shrink-0' aria-label='Filter by status'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All statuses</SelectItem>
            {STEP_FILTERS.map(s => (
              <SelectItem key={s} value={s}>
                {stepStatus(s).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    >
      {query.error ? (
        <Note error>{query.error instanceof Error ? query.error.message : 'Could not load your process steps.'}</Note>
      ) : !query.isSuccess ? (
        <Note>Loading your process steps…</Note>
      ) : all.length === 0 ? (
        <Note>No process steps are assigned to you.</Note>
      ) : (
        <>
          {rows.length > 0 ? (
            <WorkTable
              tableId={STEPS_TABLE_ID}
              columns={STEP_COLUMNS}
              sort={STEP_SORT}
              rows={rows}
              rowKey={s => s.id}
              // A removed submission leaves its assignment behind with no form to open.
              openPath={s => (s.submissionStatus === null ? null : formPath(s.submissionId))}
              cells={(s, open) => ({
                step: <TableCell className='font-medium'>{s.stepLabel ?? 'Process step'}</TableCell>,
                form: (
                  <TableCell>
                    {s.submissionStatus === null ? (
                      <span className='text-muted-foreground'>Form removed</span>
                    ) : (
                      <FormName name={s.formName} version={s.formVersion} />
                    )}
                  </TableCell>
                ),
                record: <RecordCell row={s} />,
                procedure: <ProcedureCell row={s} />,
                status: (
                  <TableCell>
                    <StatusChip status={stepStatus(s.status)} />
                  </TableCell>
                ),
                plannedStart: <DateCell at={s.plannedStart} />,
                assignedAt: <DateCell at={s.assignedAt} />,
                open: <OpenCell label={`Open ${s.stepLabel ?? 'the step'}`} onOpen={open} />,
              })}
            />
          ) : (
            <Note>No process steps match these filters.</Note>
          )}
          <p className='mt-4 text-xs text-muted-foreground'>{countLabel(rows.length, all.length, 'process step')}</p>
        </>
      )}
    </WorkCard>
  )
}
